import React, { useEffect, useState } from 'react';
import { LogOut, Image as ImageIcon, Layers, Package, Store, Sparkles, Save, Plus, Trash2, MapPin, Type } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  fetchSiteMedia,
  fetchCategories,
  fetchProducts,
  fetchStoreInfo,
  fetchPageTexts,
  adminSetSiteMedia,
  adminUpdateCategory,
  adminUpdateProduct,
  adminUpdateStoreInfo,
  adminUploadLogo,
  adminUpdatePageTexts,
} from '../../services/api';
import MediaUploader from '../../components/admin/MediaUploader';

const TABS = [
  { id: 'home', label: 'Ana Sayfa Görselleri', icon: ImageIcon },
  { id: 'logo', label: 'Logo', icon: Sparkles },
  { id: 'categories', label: 'Kategoriler', icon: Layers },
  { id: 'products', label: 'Ürünler', icon: Package },
  { id: 'store', label: 'Mağaza Bilgileri', icon: Store },
  { id: 'texts', label: 'Sayfa Metinleri', icon: Type },
];

// Curated, grouped, editable text keys (matches DEFAULT_PAGE_TEXTS in backend)
const TEXT_GROUPS = [
  { id: 'nav', title: 'Menü', keys: ['nav.home', 'nav.collections', 'nav.about', 'nav.contact'] },
  { id: 'hero', title: 'Ana Sayfa — Hero', keys: ['hero.tagline', 'hero.subtitle', 'hero.cta'] },
  { id: 'collections', title: 'Koleksiyonlar Bölümü', keys: ['collections.title', 'collections.subtitle', 'collections.viewAll'] },
  {
    id: 'about',
    title: 'Hakkımızda Sayfası',
    keys: [
      'about.subtitle', 'about.title', 'about.description',
      'about.story.p1', 'about.story.p2', 'about.story.p3',
      'about.values.heading',
      'about.value1.title', 'about.value1.description',
      'about.value2.title', 'about.value2.description',
      'about.value3.title', 'about.value3.description',
    ],
  },
  { id: 'reviews', title: 'Yorumlar Bölümü', keys: ['reviews.title', 'reviews.subtitle'] },
  {
    id: 'contact',
    title: 'İletişim Sayfası',
    keys: ['contact.title', 'contact.subtitle', 'contact.address', 'contact.whatsapp', 'contact.visitUs'],
  },
  { id: 'product', title: 'Ürün Detayı', keys: ['product.karat', 'product.contact'] },
  {
    id: 'footer',
    title: 'Footer (Alt Bölüm)',
    keys: ['footer.tagline', 'footer.quickLinks', 'footer.contactInfo', 'footer.followUs', 'footer.rights'],
  },
];

const SectionHeading = ({ children, sub }) => (
  <div className="mb-8">
    <h2 className="text-white text-2xl font-light tracking-widest uppercase">{children}</h2>
    {sub && <p className="text-white/40 text-sm mt-2">{sub}</p>}
  </div>
);

const Field = ({ label, value, onChange, type = 'text', testId }) => (
  <label className="block">
    <span className="block text-white/60 text-xs tracking-widest uppercase mb-2">{label}</span>
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
      data-testid={testId}
      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#b8860b] text-white text-sm px-4 py-3 outline-none transition-colors"
    />
  </label>
);

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('home');

  const [media, setMedia] = useState({});
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [storeInfo, setStoreInfo] = useState(null);
  const [savingStore, setSavingStore] = useState(false);
  const [storeMsg, setStoreMsg] = useState('');
  const [pageTexts, setPageTexts] = useState({}); // flat: { 'hero.tagline': {tr, en} }
  const [savingTexts, setSavingTexts] = useState(false);
  const [textsMsg, setTextsMsg] = useState('');

  const reloadAll = async () => {
    const [m, c, p, s, t] = await Promise.all([
      fetchSiteMedia(),
      fetchCategories(),
      fetchProducts(),
      fetchStoreInfo().catch(() => null),
      fetchPageTexts().catch(() => ({ flat: {} })),
    ]);
    setMedia(m || {});
    setCategories(c || []);
    setProducts(p || []);
    setStoreInfo(s);
    setPageTexts(t?.flat || {});
  };

  useEffect(() => {
    reloadAll();
  }, []);

  const handleHomeMediaUpload = async (slot, url) => {
    await adminSetSiteMedia(slot, url);
    setMedia((prev) => ({ ...prev, [slot]: url }));
  };

  const handleCategoryImage = async (id, url) => {
    await adminUpdateCategory(id, { image: url });
    setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, image: url } : c)));
  };

  const handleProductImage = async (id, url) => {
    await adminUpdateProduct(id, { image: url });
    setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, image: url } : p)));
  };

  const saveCategoryText = async (cat) => {
    const updated = await adminUpdateCategory(cat.id, {
      name: cat.name,
      description: cat.description,
    });
    setCategories((prev) => prev.map((c) => (c.id === cat.id ? updated : c)));
  };

  const saveProductText = async (prod) => {
    const updated = await adminUpdateProduct(prod.id, {
      name: prod.name,
      karat: prod.karat,
      description: prod.description,
    });
    setProducts((prev) => prev.map((p) => (p.id === prod.id ? updated : p)));
  };

  const saveStoreInfo = async () => {
    setSavingStore(true);
    setStoreMsg('');
    try {
      const updated = await adminUpdateStoreInfo(storeInfo);
      setStoreInfo(updated);
      setStoreMsg('Kaydedildi.');
      setTimeout(() => setStoreMsg(''), 2500);
    } catch (e) {
      setStoreMsg(e.response?.data?.detail || 'Hata');
    } finally {
      setSavingStore(false);
    }
  };

  const handleLogoUpload = async (file) => {
    await adminUploadLogo(file);
    // Force a reload so Header/Footer pick up the new logo
    window.location.reload();
  };

  const setText = (key, lang, value) => {
    setPageTexts((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || { tr: '', en: '' }), [lang]: value },
    }));
  };

  const saveTexts = async () => {
    setSavingTexts(true);
    setTextsMsg('');
    try {
      const updates = Object.entries(pageTexts).map(([key, vals]) => ({
        key, tr: vals?.tr ?? '', en: vals?.en ?? '',
      }));
      await adminUpdatePageTexts(updates);
      setTextsMsg('Tüm metinler kaydedildi.');
      setTimeout(() => setTextsMsg(''), 2500);
    } catch (e) {
      setTextsMsg(e.response?.data?.detail || 'Hata');
    } finally {
      setSavingTexts(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Top bar */}
      <header className="border-b border-[#1f1f1f] bg-[#0e0e0e]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Altınzade" className="h-12 w-auto" />
            <div className="hidden sm:block">
              <p className="text-white text-sm tracking-widest uppercase">Yönetici Paneli</p>
              <p className="text-white/40 text-xs">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              data-testid="admin-view-site-btn"
              className="text-xs tracking-widest uppercase text-white/60 hover:text-white px-4 py-2 border border-[#2a2a2a] hover:border-[#b8860b] transition-colors"
            >
              Siteyi Görüntüle
            </button>
            <button
              onClick={() => { logout(); navigate('/admin/login'); }}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-white/60 hover:text-white px-4 py-2 border border-[#2a2a2a] hover:border-red-500/50 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Çıkış
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
        {/* Side nav */}
        <nav className="space-y-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`w-full text-left px-4 py-3 text-sm tracking-wide flex items-center gap-3 border transition-colors ${
                  active
                    ? 'bg-[#b8860b]/10 border-[#b8860b] text-[#d4af37]'
                    : 'border-[#1f1f1f] text-white/60 hover:text-white hover:border-[#2a2a2a]'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </nav>

        {/* Main */}
        <main>
          {tab === 'home' && (
            <section data-testid="admin-section-home">
              <SectionHeading sub="Ana sayfada görünen büyük resimler.">
                Ana Sayfa Görselleri
              </SectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <MediaUploader
                  label="Hero Arka Plan"
                  currentUrl={media.hero_bg || '/hero-bg.jpg'}
                  folder="hero"
                  testIdPrefix="media-hero"
                  aspect="aspect-video"
                  onUploaded={(url) => handleHomeMediaUpload('hero_bg', url)}
                />
                <MediaUploader
                  label="Hikayemiz Görseli"
                  currentUrl={media.about_story || '/about-story.jpg'}
                  folder="about"
                  testIdPrefix="media-about"
                  aspect="aspect-[4/5]"
                  onUploaded={(url) => handleHomeMediaUpload('about_story', url)}
                />
              </div>
            </section>
          )}

          {tab === 'logo' && (
            <section data-testid="admin-section-logo">
              <SectionHeading sub="Yeni logo yüklendiğinde favicon ve PWA simgeleri otomatik yenilenir.">
                Logo
              </SectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-[#141414] border border-[#2a2a2a] p-5">
                  <h4 className="text-white text-sm tracking-widest uppercase mb-4">Mevcut Logo</h4>
                  <div className="bg-[#0a0a0a] border border-[#2a2a2a] aspect-square flex items-center justify-center p-8">
                    <img src="/logo.png?v=2" alt="Logo" className="max-h-full max-w-full object-contain" data-testid="admin-current-logo" />
                  </div>
                </div>
                <div className="bg-[#141414] border border-[#2a2a2a] p-5">
                  <h4 className="text-white text-sm tracking-widest uppercase mb-4">Yeni Logo Yükle</h4>
                  <p className="text-white/50 text-xs mb-6 leading-relaxed">
                    Önerilen: PNG, kare oran (1:1), şeffaf veya siyah arka plan. Siyah arka plan otomatik şeffaf yapılır.
                  </p>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => e.target.files?.[0] && handleLogoUpload(e.target.files[0])}
                    data-testid="admin-logo-input"
                    className="block w-full text-white/70 text-xs file:mr-4 file:py-3 file:px-5 file:border-0 file:bg-[#b8860b] file:text-black file:text-xs file:tracking-widest file:uppercase file:cursor-pointer hover:file:bg-[#d4af37]"
                  />
                  <p className="text-white/40 text-xs mt-4">
                    Yüklendikten sonra sayfa otomatik yenilenir.
                  </p>
                </div>
              </div>
            </section>
          )}

          {tab === 'categories' && (
            <section data-testid="admin-section-categories">
              <SectionHeading sub="Kategori kapaklarını ve metinleri düzenleyin.">
                Kategoriler
              </SectionHeading>
              <div className="space-y-8">
                {categories.map((cat) => (
                  <div key={cat.id} className="bg-[#141414] border border-[#2a2a2a] p-6 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6" data-testid={`admin-category-${cat.id}`}>
                    <MediaUploader
                      label={`Kapak: ${cat.name?.tr || cat.id}`}
                      currentUrl={cat.image}
                      folder={`categories/${cat.id}`}
                      testIdPrefix={`category-${cat.id}-cover`}
                      aspect="aspect-[4/5]"
                      onUploaded={(url) => handleCategoryImage(cat.id, url)}
                    />
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field
                          label="İsim (TR)"
                          value={cat.name?.tr}
                          onChange={(v) => setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, name: { ...c.name, tr: v } } : c)))}
                          testId={`category-${cat.id}-name-tr`}
                        />
                        <Field
                          label="Name (EN)"
                          value={cat.name?.en}
                          onChange={(v) => setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, name: { ...c.name, en: v } } : c)))}
                          testId={`category-${cat.id}-name-en`}
                        />
                        <Field
                          label="Açıklama (TR)"
                          value={cat.description?.tr}
                          onChange={(v) => setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, description: { ...c.description, tr: v } } : c)))}
                          testId={`category-${cat.id}-desc-tr`}
                        />
                        <Field
                          label="Description (EN)"
                          value={cat.description?.en}
                          onChange={(v) => setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, description: { ...c.description, en: v } } : c)))}
                          testId={`category-${cat.id}-desc-en`}
                        />
                      </div>
                      <button
                        onClick={() => saveCategoryText(cat)}
                        data-testid={`category-${cat.id}-save-btn`}
                        className="inline-flex items-center gap-2 bg-[#b8860b] hover:bg-[#d4af37] text-black text-xs tracking-widest uppercase px-6 py-3 transition-colors"
                      >
                        <Save className="w-3.5 h-3.5" /> Metni Kaydet
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {tab === 'products' && (
            <section data-testid="admin-section-products">
              <SectionHeading sub={`${products.length} ürün. Resim, isim, ayar ve açıklamaları düzenleyin.`}>
                Ürünler
              </SectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {products.map((prod) => (
                  <div key={prod.id} className="bg-[#141414] border border-[#2a2a2a] p-5 space-y-4" data-testid={`admin-product-${prod.id}`}>
                    <MediaUploader
                      label={`#${prod.id} ${prod.name}`}
                      currentUrl={prod.image}
                      folder={`products/${prod.id}`}
                      testIdPrefix={`product-${prod.id}-cover`}
                      aspect="aspect-square"
                      onUploaded={(url) => handleProductImage(prod.id, url)}
                    />
                    <Field
                      label="İsim"
                      value={prod.name}
                      onChange={(v) => setProducts((prev) => prev.map((p) => (p.id === prod.id ? { ...p, name: v } : p)))}
                      testId={`product-${prod.id}-name`}
                    />
                    <Field
                      label="Ayar"
                      type="number"
                      value={prod.karat}
                      onChange={(v) => setProducts((prev) => prev.map((p) => (p.id === prod.id ? { ...p, karat: v } : p)))}
                      testId={`product-${prod.id}-karat`}
                    />
                    <Field
                      label="Açıklama (TR)"
                      value={prod.description?.tr}
                      onChange={(v) => setProducts((prev) => prev.map((p) => (p.id === prod.id ? { ...p, description: { ...p.description, tr: v } } : p)))}
                      testId={`product-${prod.id}-desc-tr`}
                    />
                    <Field
                      label="Description (EN)"
                      value={prod.description?.en}
                      onChange={(v) => setProducts((prev) => prev.map((p) => (p.id === prod.id ? { ...p, description: { ...p.description, en: v } } : p)))}
                      testId={`product-${prod.id}-desc-en`}
                    />
                    <button
                      onClick={() => saveProductText(prod)}
                      data-testid={`product-${prod.id}-save-btn`}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#b8860b] hover:bg-[#d4af37] text-black text-xs tracking-widest uppercase py-3 transition-colors"
                    >
                      <Save className="w-3.5 h-3.5" /> Kaydet
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {tab === 'store' && (
            <section data-testid="admin-section-store">
              <SectionHeading sub="Adres, telefon, e-posta, çalışma saatleri ve sosyal medya — iletişim sayfasında görünen tüm bilgiler.">
                Mağaza Bilgileri
              </SectionHeading>
              {storeInfo && (
                <div className="space-y-6">
                  {/* Basic info */}
                  <div className="bg-[#141414] border border-[#2a2a2a] p-6">
                    <h3 className="text-white text-sm tracking-widest uppercase mb-5">Temel Bilgiler</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="Mağaza Adı (TR)" value={storeInfo.name} onChange={(v) => setStoreInfo({ ...storeInfo, name: v })} testId="store-name-tr" />
                      <Field label="Store Name (EN)" value={storeInfo.nameEn} onChange={(v) => setStoreInfo({ ...storeInfo, nameEn: v })} testId="store-name-en" />
                      <Field label="E-posta" type="email" value={storeInfo.email} onChange={(v) => setStoreInfo({ ...storeInfo, email: v })} testId="store-email" />
                      <Field label="Instagram kullanıcı adı" value={storeInfo.instagram} onChange={(v) => setStoreInfo({ ...storeInfo, instagram: v })} testId="store-instagram" />
                      <Field label="WhatsApp (uluslararası, sadece rakam)" value={storeInfo.whatsapp} onChange={(v) => setStoreInfo({ ...storeInfo, whatsapp: v })} testId="store-whatsapp" />
                      <Field label="WhatsApp (görüntülenen)" value={storeInfo.whatsappDisplay} onChange={(v) => setStoreInfo({ ...storeInfo, whatsappDisplay: v })} testId="store-whatsapp-display" />
                      <Field label="Çalışma Saatleri (TR)" value={storeInfo.hours_tr} onChange={(v) => setStoreInfo({ ...storeInfo, hours_tr: v })} testId="store-hours-tr" />
                      <Field label="Working Hours (EN)" value={storeInfo.hours_en} onChange={(v) => setStoreInfo({ ...storeInfo, hours_en: v })} testId="store-hours-en" />
                    </div>
                  </div>

                  {/* Addresses */}
                  <div className="bg-[#141414] border border-[#2a2a2a] p-6">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="text-white text-sm tracking-widest uppercase flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#b8860b]" /> Adresler
                      </h3>
                      <button
                        type="button"
                        onClick={() => setStoreInfo({
                          ...storeInfo,
                          addresses: [
                            ...(storeInfo.addresses || []),
                            { label_tr: '', label_en: '', address_tr: '', address_en: '', maps_url: '' },
                          ],
                        })}
                        data-testid="store-add-address-btn"
                        className="inline-flex items-center gap-1.5 text-xs tracking-widest uppercase text-[#b8860b] hover:text-[#d4af37] border border-[#b8860b]/40 hover:border-[#b8860b] px-3 py-2 transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" /> Yeni Adres
                      </button>
                    </div>

                    <div className="space-y-5">
                      {(storeInfo.addresses || []).map((addr, idx) => (
                        <div key={addr.id || idx} className="bg-[#0a0a0a] border border-[#1f1f1f] p-5" data-testid={`store-address-${idx}`}>
                          <div className="flex items-center justify-between mb-4">
                            <span className="text-white/40 text-xs tracking-widest uppercase">Adres #{idx + 1}</span>
                            <button
                              type="button"
                              onClick={() => setStoreInfo({
                                ...storeInfo,
                                addresses: storeInfo.addresses.filter((_, i) => i !== idx),
                              })}
                              data-testid={`store-address-${idx}-delete-btn`}
                              className="inline-flex items-center gap-1 text-xs text-red-400/70 hover:text-red-400 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Sil
                            </button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field
                              label="Etiket (TR) — örn. 'Lefkoşa Mağazası'"
                              value={addr.label_tr}
                              onChange={(v) => setStoreInfo({
                                ...storeInfo,
                                addresses: storeInfo.addresses.map((a, i) => (i === idx ? { ...a, label_tr: v } : a)),
                              })}
                              testId={`store-address-${idx}-label-tr`}
                            />
                            <Field
                              label="Label (EN) — e.g. 'Nicosia Store'"
                              value={addr.label_en}
                              onChange={(v) => setStoreInfo({
                                ...storeInfo,
                                addresses: storeInfo.addresses.map((a, i) => (i === idx ? { ...a, label_en: v } : a)),
                              })}
                              testId={`store-address-${idx}-label-en`}
                            />
                            <Field
                              label="Adres (TR)"
                              value={addr.address_tr}
                              onChange={(v) => setStoreInfo({
                                ...storeInfo,
                                addresses: storeInfo.addresses.map((a, i) => (i === idx ? { ...a, address_tr: v } : a)),
                              })}
                              testId={`store-address-${idx}-address-tr`}
                            />
                            <Field
                              label="Address (EN)"
                              value={addr.address_en}
                              onChange={(v) => setStoreInfo({
                                ...storeInfo,
                                addresses: storeInfo.addresses.map((a, i) => (i === idx ? { ...a, address_en: v } : a)),
                              })}
                              testId={`store-address-${idx}-address-en`}
                            />
                            <div className="md:col-span-2">
                              <Field
                                label="Google Maps URL (paylaşım veya gömme bağlantısı)"
                                value={addr.maps_url}
                                onChange={(v) => setStoreInfo({
                                  ...storeInfo,
                                  addresses: storeInfo.addresses.map((a, i) => (i === idx ? { ...a, maps_url: v } : a)),
                                })}
                                testId={`store-address-${idx}-maps-url`}
                              />
                              <p className="text-white/40 text-xs mt-1.5 leading-relaxed">
                                Google Maps'te konumu açın → "Paylaş" → "Bir bağlantıyı kopyala" sekmesinden URL'yi yapıştırın. Hem normal paylaşım URL'si hem de "Haritayı yerleştir" iframe URL'si desteklenir; iletişim sayfası otomatik olarak yerleşik harita gösterir.
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                      {(storeInfo.addresses || []).length === 0 && (
                        <p className="text-white/40 text-sm">Henüz adres eklenmedi. "Yeni Adres" düğmesine basın.</p>
                      )}
                    </div>
                  </div>

                  {/* Save button */}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={saveStoreInfo}
                      disabled={savingStore}
                      data-testid="store-save-btn"
                      className="inline-flex items-center gap-2 bg-[#b8860b] hover:bg-[#d4af37] disabled:opacity-50 text-black text-xs tracking-widest uppercase px-10 py-4 transition-colors"
                    >
                      <Save className="w-3.5 h-3.5" /> {savingStore ? 'Kaydediliyor…' : 'Tüm Değişiklikleri Kaydet'}
                    </button>
                    {storeMsg && <span className="text-white/60 text-xs">{storeMsg}</span>}
                  </div>
                </div>
              )}
            </section>
          )}

          {tab === 'texts' && (
            <section data-testid="admin-section-texts">
              <SectionHeading sub="Tüm sitede görünen statik metinleri (TR ve EN) buradan düzenleyin. Boş bırakılan alanlar varsayılan değere döner.">
                Sayfa Metinleri
              </SectionHeading>

              <div className="space-y-6">
                {TEXT_GROUPS.map((group) => (
                  <div key={group.id} className="bg-[#141414] border border-[#2a2a2a] p-6" data-testid={`texts-group-${group.id}`}>
                    <h3 className="text-white text-sm tracking-widest uppercase mb-5">{group.title}</h3>
                    <div className="space-y-4">
                      {group.keys.map((key) => {
                        const vals = pageTexts[key] || { tr: '', en: '' };
                        const isLong = (vals.tr || '').length > 80 || (vals.en || '').length > 80
                          || key.endsWith('description') || key.includes('story.') || key.endsWith('tagline');
                        return (
                          <div key={key} className="border-t border-[#1f1f1f] pt-4 first:border-t-0 first:pt-0">
                            <p className="text-white/40 text-xs mb-3 font-mono">{key}</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <label className="block">
                                <span className="block text-white/60 text-xs tracking-widest uppercase mb-2">Türkçe</span>
                                {isLong ? (
                                  <textarea
                                    value={vals.tr || ''}
                                    onChange={(e) => setText(key, 'tr', e.target.value)}
                                    rows={3}
                                    data-testid={`text-${key}-tr`}
                                    className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#b8860b] text-white text-sm px-4 py-3 outline-none transition-colors leading-relaxed"
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    value={vals.tr || ''}
                                    onChange={(e) => setText(key, 'tr', e.target.value)}
                                    data-testid={`text-${key}-tr`}
                                    className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#b8860b] text-white text-sm px-4 py-3 outline-none transition-colors"
                                  />
                                )}
                              </label>
                              <label className="block">
                                <span className="block text-white/60 text-xs tracking-widest uppercase mb-2">English</span>
                                {isLong ? (
                                  <textarea
                                    value={vals.en || ''}
                                    onChange={(e) => setText(key, 'en', e.target.value)}
                                    rows={3}
                                    data-testid={`text-${key}-en`}
                                    className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#b8860b] text-white text-sm px-4 py-3 outline-none transition-colors leading-relaxed"
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    value={vals.en || ''}
                                    onChange={(e) => setText(key, 'en', e.target.value)}
                                    data-testid={`text-${key}-en`}
                                    className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#b8860b] text-white text-sm px-4 py-3 outline-none transition-colors"
                                  />
                                )}
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                <div className="flex items-center gap-4">
                  <button
                    onClick={saveTexts}
                    disabled={savingTexts}
                    data-testid="texts-save-btn"
                    className="inline-flex items-center gap-2 bg-[#b8860b] hover:bg-[#d4af37] disabled:opacity-50 text-black text-xs tracking-widest uppercase px-10 py-4 transition-colors"
                  >
                    <Save className="w-3.5 h-3.5" /> {savingTexts ? 'Kaydediliyor…' : 'Tüm Metinleri Kaydet'}
                  </button>
                  {textsMsg && <span className="text-white/60 text-xs">{textsMsg}</span>}
                </div>
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
